import { useCallback, useEffect, useRef, useState } from "react";
import type {
  MdmPipelineStageEvent,
  MdmRun,
  RunStatus,
  TriggerMode,
} from "../types";
import {
  executeRun,
  getRunStatus,
  autoDecideAllPairs,
  cancelRun,
  resetRun,
} from "../api/mdm-client";
import { useMdmRunStore } from "../store/useMdmRunStore";

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

type StageKey = "intake" | "blocking" | "scoring" | "hitl" | "golden_record";

interface StageState {
  status: "pending" | "running" | "complete" | "failed";
  records_processed?: number;
  duration_ms?: number;
  error?: string;
}

interface RunProjection {
  candidatePairs: number;
  estimatedHitlSize: number;
  estimatedDurationMin: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STAGE_ORDER: StageKey[] = [
  "intake",
  "blocking",
  "scoring",
  "hitl",
  "golden_record",
];

const STAGE_LABELS: Record<StageKey, string> = {
  intake: "Intake",
  blocking: "Grouping",
  scoring: "Scoring",
  hitl: "Manual Review",
  golden_record: "Golden Record",
};

const STAGE_ICONS: Record<StageKey, string> = {
  intake: "⬇",
  blocking: "⧉",
  scoring: "⚖",
  hitl: "👤",
  golden_record: "✦",
};

export type DerivedStageStatus = "pending" | "running" | "complete" | "failed";

// Backend fine-grained run status → UI stage index (intake=0 … golden=4).
const STAGE_BY_STATUS: Record<string, number> = {
  created: 0,
  running: 0,
  normalizing: 0,
  blocking: 1,
  scoring: 2,
  classifying: 2,
  adjudicating: 3,
  clustering: 3,
  survivorship: 4,
};

/**
 * Derive each UI pipeline stage (intake → blocking → scoring → manual-review →
 * golden) purely from the pipeline's POSITION — earlier stages complete, the
 * current one running, later ones pending. This deliberately does NOT key
 * completion off stats flags: the old logic did, and when a flag was absent it
 * left Intake/Grouping "pending" while later stages showed complete (the
 * out-of-order display bug). Position-based derivation is monotonic by
 * construction and cannot produce that inconsistency. Exported + pure so the
 * invariant is unit-testable without a browser.
 */
export function derivePipelineStages(
  status: string,
  opts: {
    isPaused?: boolean;
    hasPairs?: boolean;
    hasGolden?: boolean;
    hasHitlRouted?: boolean;
  } = {},
): Record<StageKey, DerivedStageStatus> {
  const {
    isPaused = false,
    hasPairs = false,
    hasGolden = false,
    hasHitlRouted = false,
  } = opts;
  const stageMap: Record<StageKey, DerivedStageStatus> = {
    intake: "pending",
    blocking: "pending",
    scoring: "pending",
    hitl: "pending",
    golden_record: "pending",
  };

  if (status === "completed" || status === "partially_complete") {
    for (const k of STAGE_ORDER) stageMap[k] = "complete";
  } else if (
    status === "failed" ||
    status === "error" ||
    status === "cancelled"
  ) {
    // Best-effort: mark stages complete up to the furthest point we have
    // evidence for, then flag the next stage as the failure point.
    let reached = 0;
    if (hasPairs) reached = Math.max(reached, 2);
    if (hasHitlRouted) reached = Math.max(reached, 3);
    if (hasGolden) reached = Math.max(reached, 4);
    STAGE_ORDER.forEach((k, i) => {
      stageMap[k] =
        i < reached ? "complete" : i === reached ? "failed" : "pending";
    });
  } else {
    const idx = STAGE_BY_STATUS[status] ?? 0;
    STAGE_ORDER.forEach((k, i) => {
      stageMap[k] = i < idx ? "complete" : i === idx ? "running" : "pending";
    });
    // Paused = awaiting manual review: everything up to the review stage is
    // done and the review stage is the one actively "running".
    if (isPaused) {
      stageMap.intake = "complete";
      stageMap.blocking = "complete";
      stageMap.scoring = "complete";
      stageMap.hitl = "running";
      if (stageMap.golden_record !== "complete")
        stageMap.golden_record = "pending";
    }
  }
  return stageMap;
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function formatTs(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Minimal cron expression → human-readable text mapping. */
function parseCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return "Custom schedule";
  const [min, hour, dom, , dow] = parts;
  const hourNum = parseInt(hour, 10);
  const minNum = parseInt(min, 10);
  const timeStr =
    !isNaN(hourNum) && !isNaN(minNum)
      ? `${hourNum % 12 || 12}:${String(minNum).padStart(2, "0")} ${hourNum < 12 ? "AM" : "PM"}`
      : `${hour}:${min}`;
  if (dom === "*" && dow === "1-5") return `Every weekday at ${timeStr}`;
  if (dom === "*" && dow === "*") return `Every day at ${timeStr}`;
  if (dom === "*" && dow === "0") return `Every Sunday at ${timeStr}`;
  if (dom === "*" && dow === "6") return `Every Saturday at ${timeStr}`;
  if (dom === "1" && dow === "*") return `Monthly (1st) at ${timeStr}`;
  return `${expr} (custom)`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// ---- Stage badge ----------------------------------------------------------

function StageBadge({ status }: { status: StageState["status"] }) {
  const map: Record<StageState["status"], { label: string; cls: string }> = {
    pending: { label: "Pending", cls: "ztd-badge" },
    running: {
      label: "Running",
      cls: "ztd-badge ztd-badge-info animate-pulse",
    },
    complete: { label: "Complete", cls: "ztd-badge ztd-badge-success" },
    failed: { label: "Failed", cls: "ztd-badge ztd-badge-critical" },
  };
  const { label, cls } = map[status];
  return <span className={cls}>{label}</span>;
}

// ---- Run progress card ----------------------------------------------------

interface MdmRunProgressCardProps {
  stages: Record<string, StageState>;
  events: MdmPipelineStageEvent[];
}

function MdmRunProgressCard({ stages }: MdmRunProgressCardProps) {
  return (
    <div className="rounded-xl border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-5 backdrop-blur-sm">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white/50">
        Pipeline Progress
      </h3>
      <ol className="relative space-y-0">
        {STAGE_ORDER.map((key, idx) => {
          const stage: StageState = stages[key] ?? { status: "pending" };
          const isLast = idx === STAGE_ORDER.length - 1;
          const isRunning = stage.status === "running";

          return (
            <li
              key={key}
              data-testid={`mdm-stage-${key}`}
              data-status={stage.status}
              className="flex gap-4"
            >
              {/* Left: icon + connector line */}
              <div className="flex flex-col items-center">
                <div
                  className={`
                    relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm
                    ${stage.status === "complete" ? "border-success/50 bg-success/15 text-success" : ""}
                    ${stage.status === "running" ? "border-info/50 bg-info/15 text-info" : ""}
                    ${stage.status === "failed" ? "border-error/50 bg-error/15 text-error" : ""}
                    ${stage.status === "pending" ? "border-[var(--border-strong)] bg-[var(--overlay-subtle)] text-[var(--text-primary)]" : ""}
                  `}
                >
                  {STAGE_ICONS[key]}
                  {isRunning && (
                    <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-info">
                      <span className="absolute inset-0 animate-ping rounded-full bg-info opacity-75" />
                    </span>
                  )}
                </div>
                {!isLast && (
                  <div
                    className={`mt-1 w-px flex-1 ${
                      stage.status === "complete"
                        ? "bg-success/40"
                        : "bg-[var(--overlay-strong)]"
                    }`}
                    style={{ minHeight: "2rem" }}
                  />
                )}
              </div>

              {/* Right: content */}
              <div className={`pb-6 ${isLast ? "pb-0" : ""} flex-1`}>
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-medium ${
                      stage.status === "pending"
                        ? "text-[var(--text-primary)]"
                        : "text-white/90"
                    }`}
                  >
                    {STAGE_LABELS[key]}
                  </span>
                  <StageBadge status={stage.status} />
                </div>
                {(stage.records_processed != null ||
                  stage.duration_ms != null ||
                  stage.error) && (
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-[var(--text-primary)]">
                    {stage.records_processed != null && (
                      <span>
                        {stage.records_processed.toLocaleString()} records
                      </span>
                    )}
                    {stage.duration_ms != null && (
                      <span>{formatMs(stage.duration_ms)}</span>
                    )}
                    {stage.error && (
                      <span className="text-error">{stage.error}</span>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ---- Trigger config panel -------------------------------------------------

interface MdmTriggerConfigPanelProps {
  run: MdmRun;
  triggerMode: TriggerMode;
  onModeChange: (mode: TriggerMode) => void;
  onRunNow: () => void;
  onCancelRun: () => void;
  cancelling: boolean;
  cronExpr: string;
  onCronChange: (expr: string) => void;
}

const ACTIVE_PIPELINE_STAGES = [
  "running",
  "normalizing",
  "blocking",
  "scoring",
  "classifying",
  "adjudicating",
  "clustering",
  "survivorship",
];

function MdmTriggerConfigPanel({
  run,
  triggerMode,
  onModeChange,
  onRunNow,
  onCancelRun,
  cancelling,
  cronExpr,
  onCronChange,
}: MdmTriggerConfigPanelProps) {
  const modes: { key: TriggerMode; label: string; desc: string }[] = [
    { key: "auto", label: "Auto", desc: "Trigger on data load automatically" },
    { key: "manual", label: "Manual", desc: "Trigger runs explicitly" },
    { key: "scheduled", label: "Scheduled", desc: "Run on a cron schedule" },
  ];

  return (
    <div className="rounded-xl border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-5 backdrop-blur-sm">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white/50">
        Trigger Configuration
      </h3>

      {/* Mode radio cards */}
      <div className="mb-5 grid grid-cols-3 gap-3">
        {modes.map(({ key, label, desc }) => (
          <button
            key={key}
            onClick={() => onModeChange(key)}
            className={`
              rounded-lg border p-3 text-left transition-all
              ${
                triggerMode === key
                  ? "border-indigo-500/60 bg-indigo-500/10 ring-1 ring-indigo-500/30"
                  : "border-[var(--border-muted)] bg-[var(--overlay-subtle)] hover:border-[var(--border-strong)] hover:bg-[var(--overlay-medium)]"
              }
            `}
          >
            <div className="flex items-center gap-2">
              <span
                className={`h-3.5 w-3.5 rounded-full border-2 ${
                  triggerMode === key
                    ? "border-indigo-400 bg-indigo-400"
                    : "border-[var(--border-strong)] bg-transparent"
                }`}
              />
              <span className="text-sm font-medium text-white/90">{label}</span>
            </div>
            <p className="mt-1.5 text-xs text-[var(--text-primary)]">{desc}</p>
          </button>
        ))}
      </div>

      {/* Mode-specific content */}
      {triggerMode === "auto" && (
        <div className="space-y-3">
          {/* Automation confidence moment card */}
          <div className="rounded-lg border border-indigo-500/20 bg-indigo-500/8 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-indigo-300/70">
              Automation Readiness Summary
            </p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-[var(--text-primary)]">Entity type</dt>
                <dd className="font-medium capitalize text-white/90">
                  {run.entity_type}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--text-primary)]">Sources</dt>
                <dd className="font-medium text-white/90">
                  {run.source_count}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--text-primary)]">
                  Est. candidate pairs
                </dt>
                <dd className="font-medium text-white/90">
                  {run.stats?.candidate_pairs_generated != null
                    ? run.stats.candidate_pairs_generated.toLocaleString()
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--text-primary)]">
                  Manual Review quota
                </dt>
                <dd className="font-medium text-white/90">
                  {run.stats?.review_pending != null
                    ? `${run.stats.review_pending.toLocaleString()} pairs`
                    : "—"}
                </dd>
              </div>
            </dl>
          </div>

          <button
            className="w-full rounded-lg border border-emerald-500/40 bg-emerald-500/10 py-2.5 text-sm font-semibold text-emerald-300 transition hover:bg-emerald-500/20 active:scale-[0.98]"
            onClick={() => {}}
          >
            Enable automatic MDM on data load
          </button>

          <div className="text-center">
            <button
              className="text-xs text-[var(--text-primary)] underline-offset-2 hover:text-white/60 hover:underline"
              onClick={() => onModeChange("manual")}
            >
              Switch to Manual
            </button>
          </div>
        </div>
      )}

      {triggerMode === "manual" && (
        <div className="space-y-3">
          <p className="text-sm text-white/50">
            {ACTIVE_PIPELINE_STAGES.includes(run.status)
              ? "Pipeline is currently running."
              : run.status === "completed"
                ? "Run completed. Trigger a new run to reprocess."
                : run.status === "cancelled"
                  ? "Run cancelled. Trigger a new run to reprocess."
                  : "Click the button below to start an MDM run manually."}
          </p>
          <button
            disabled={ACTIVE_PIPELINE_STAGES.includes(run.status)}
            onClick={onRunNow}
            data-testid="mdm-run-execute"
            className="w-full rounded-lg border border-[rgba(134,188,37,0.4)] bg-[rgba(134,188,37,0.1)] py-2.5 text-sm font-semibold text-[#86bc25] transition hover:bg-[rgba(134,188,37,0.2)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {ACTIVE_PIPELINE_STAGES.includes(run.status)
              ? "Running…"
              : run.status === "completed" || run.status === "cancelled"
                ? "Re-run MDM"
                : "Run MDM"}
          </button>
          {ACTIVE_PIPELINE_STAGES.includes(run.status) && (
            <button
              onClick={onCancelRun}
              disabled={cancelling}
              data-testid="mdm-run-cancel"
              className="w-full rounded-lg border border-red-500/30 bg-red-500/10 py-2 text-xs font-medium text-red-300 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {cancelling ? "Cancelling…" : "Cancel Run"}
            </button>
          )}
        </div>
      )}

      {triggerMode === "scheduled" && (
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-white/50">
              Cron Expression
            </label>
            <input
              type="text"
              value={cronExpr}
              onChange={(e) => onCronChange(e.target.value)}
              placeholder="0 2 * * 1-5"
              className="w-full rounded-lg border border-[var(--border-strong)] bg-[var(--overlay-subtle)] px-3 py-2 font-mono text-sm text-white/90 placeholder-white/25 focus:border-indigo-500/50 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
            />
          </div>
          {cronExpr.trim() && (
            <div className="rounded-lg border border-[var(--border-muted)] bg-[var(--overlay-subtle)] px-3 py-2 text-xs text-white/60">
              <span className="mr-2 text-[var(--text-primary)]">Preview:</span>
              {parseCron(cronExpr)}
            </div>
          )}
          <button className="w-full rounded-lg border border-indigo-500/40 bg-indigo-500/10 py-2.5 text-sm font-semibold text-indigo-300 transition hover:bg-indigo-500/20 active:scale-[0.98]">
            Save Schedule
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Pre-trigger modal ----------------------------------------------------

interface PreTriggerModalProps {
  projection: RunProjection;
  onConfirm: () => void;
  onCancel: () => void;
}

function PreTriggerModal({
  projection,
  onConfirm,
  onCancel,
}: PreTriggerModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-[var(--border-strong)] bg-zinc-900/90 p-6 shadow-2xl backdrop-blur-xl">
        <h2 className="mb-1 text-base font-semibold text-[var(--text-primary)]">
          Confirm MDM Run
        </h2>
        <p className="mb-5 text-sm text-white/50">
          Review projected run metrics before starting.
        </p>

        <dl className="mb-6 grid grid-cols-3 gap-3 text-center">
          {[
            {
              label: "Candidate Pairs",
              value: projection.candidatePairs.toLocaleString(),
            },
            {
              label: "Est. Manual Review Size",
              value: projection.estimatedHitlSize.toLocaleString(),
            },
            {
              label: "Est. Duration",
              value: `~${projection.estimatedDurationMin}m`,
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="rounded-lg border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-3"
            >
              <div className="text-lg font-bold text-[var(--text-primary)]">
                {value}
              </div>
              <div className="mt-0.5 text-xs text-[var(--text-primary)]">
                {label}
              </div>
            </div>
          ))}
        </dl>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-[var(--border-strong)] bg-[var(--overlay-subtle)] py-2.5 text-sm font-medium text-white/60 transition hover:bg-[var(--overlay-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 rounded-lg border border-indigo-500/40 bg-indigo-500/20 py-2.5 text-sm font-semibold text-indigo-200 transition hover:bg-indigo-500/30 active:scale-[0.98]"
          >
            Start Run
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Run stats summary ----------------------------------------------------

interface RunStatsSummaryProps {
  run: MdmRun;
  onNavigateToGoldenRecords: () => void;
}

function RunStatsSummary({
  run,
  onNavigateToGoldenRecords,
}: RunStatsSummaryProps) {
  const stats = run.stats;
  const raw =
    (run as unknown as { _raw_stats?: Record<string, unknown> })._raw_stats ??
    {};
  if (!stats && !Object.keys(raw).length) return null;

  const get = (
    frontendKey: string,
    ...fallbacks: string[]
  ): number | undefined => {
    const keys = [frontendKey, ...fallbacks];
    for (const k of keys) {
      const v = (stats as unknown as Record<string, unknown>)?.[k] ?? raw[k];
      if (v != null) return Number(v);
    }
    return undefined;
  };

  const tiles = [
    {
      label: "Clusters",
      value: get("clusters_produced", "golden_records_created"),
      color: "text-indigo-300",
    },
    {
      label: "Records",
      value: get("records_processed", "source_records_total"),
      color: "text-sky-300",
    },
    {
      label: "Pairs",
      value: get("candidate_pairs_generated", "pairs_scored"),
      color: "text-white/70",
    },
    {
      label: "Auto-matched",
      value: get("hitl_bypass_count", "auto_matched"),
      color: "text-emerald-300",
    },
    {
      label: "Pending Review",
      value: get("review_pending"),
      color: "text-amber-300",
    },
  ];

  return (
    <div className="rounded-xl border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-5 backdrop-blur-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-white/50">
          Run Summary
        </h3>
        <button
          onClick={onNavigateToGoldenRecords}
          className="text-xs font-medium text-indigo-400 underline-offset-2 hover:underline"
        >
          View golden records →
        </button>
      </div>
      <div className="grid grid-cols-5 gap-3">
        {tiles.map(({ label, value, color }) => (
          <div
            key={label}
            className="rounded-lg border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-3 text-center"
          >
            <div className={`text-xl font-bold ${color}`}>
              {value != null ? value.toLocaleString() : "—"}
            </div>
            <div className="mt-1 text-xs text-[var(--text-primary)]">
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Last run card (auto mode) --------------------------------------------

interface LastRunCardProps {
  run: MdmRun;
  onNavigateToGoldenRecords: () => void;
  onRerun: () => void;
}

function LastRunCard({
  run,
  onNavigateToGoldenRecords,
  onRerun,
}: LastRunCardProps) {
  const outcomeMap: Partial<Record<RunStatus, { label: string; cls: string }>> =
    {
      completed: { label: "Completed", cls: "ztd-badge ztd-badge-success" },
      failed: { label: "Failed", cls: "ztd-badge ztd-badge-critical" },
      cancelled: { label: "Cancelled", cls: "ztd-badge ztd-badge-warning" },
      partially_complete: {
        label: "Partial",
        cls: "ztd-badge ztd-badge-warning",
      },
    };
  const outcome = run.status ? outcomeMap[run.status] : undefined;

  return (
    <div className="rounded-xl border border-[var(--border-muted)] bg-[var(--overlay-subtle)] p-5 backdrop-blur-sm">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white/50">
        Last Auto Run
      </h3>
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="text-sm text-white/70">
            <span className="text-[var(--text-primary)]">Run:</span>{" "}
            <span className="font-medium text-white/90">{run.name}</span>
          </div>
          <div className="text-sm text-white/70">
            <span className="text-[var(--text-primary)]">Triggered:</span>{" "}
            <span>{formatTs(run.updated_at)}</span>
          </div>
          {run.stats && (
            <div className="text-sm text-white/70">
              <span className="text-[var(--text-primary)]">
                Golden records:
              </span>{" "}
              <span className="font-medium text-indigo-300">
                {(
                  run.stats.golden_records ??
                  run.stats.clusters_produced ??
                  0
                ).toLocaleString()}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {outcome && <span className={outcome.cls}>{outcome.label}</span>}
          {(run.status === "failed" || run.status === "cancelled") && (
            <button
              onClick={onRerun}
              className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 transition hover:bg-red-500/20"
            >
              Re-run
            </button>
          )}
          {run.status === "completed" && (
            <button
              onClick={onNavigateToGoldenRecords}
              className="text-xs font-medium text-indigo-400 underline-offset-2 hover:underline"
            >
              View golden records →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- HITL queue entry point -----------------------------------------------

interface HitlQueueEntryProps {
  pendingReviewCount: number;
  runId: string;
  onNavigateToReview: () => void;
}

function HitlQueueEntry({
  pendingReviewCount,
  runId: _runId,
  onNavigateToReview,
}: HitlQueueEntryProps) {
  if (pendingReviewCount === 0) return null;

  return (
    <button
      onClick={onNavigateToReview}
      data-testid="mdm-hitl-open-review-queue"
      className="flex w-full items-center justify-between rounded-xl border border-amber-500/30 bg-amber-500/8 p-4 backdrop-blur-sm transition hover:border-amber-500/50 hover:bg-amber-500/12"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/20 text-sm">
          👤
        </span>
        <div className="text-left">
          <p className="text-sm font-semibold text-amber-200">
            {pendingReviewCount.toLocaleString()} pair
            {pendingReviewCount !== 1 ? "s" : ""} await review
          </p>
          <p className="text-xs text-amber-300/60">
            Manual review decisions required
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-amber-400 px-2 py-0.5 text-xs font-bold text-amber-900">
          {pendingReviewCount}
        </span>
        <span className="text-amber-400/60">→</span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// HITL Resume Banner
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// P5: HITL Resume Banner — gated auto-bypass
// Primary CTA is "Go to Review Queue".  Auto-decide is collapsed under an
// admin-override <details> block and requires a non-empty justification
// before either bypass button becomes active.
// ---------------------------------------------------------------------------

function HitlResumeBanner({
  runId,
  pendingCount,
  onResumed,
  onNavigateToReview,
}: {
  runId: string;
  pendingCount: number;
  onResumed: () => void;
  onNavigateToReview?: () => void;
}) {
  const [busy, setBusy] = useState<"match" | "no_match" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // P5: bypass reason required before auto-decide buttons become active
  const [bypassReason, setBypassReason] = useState("");
  const bypassAllowed = bypassReason.trim().length >= 10;

  const handleAutoDecide = async (decision: "match" | "no_match") => {
    if (!bypassAllowed) return;
    setBusy(decision);
    setMsg(null);
    try {
      const r = await autoDecideAllPairs(runId, decision, bypassReason.trim());
      const verb = decision === "match" ? "Auto-matched" : "Auto-declined";
      setMsg(
        r.decided > 0
          ? `${verb} ${r.decided} pairs. Resuming pipeline…`
          : "Resuming pipeline…",
      );
      await executeRun(runId, { stage: "clustering" });
      setTimeout(onResumed, 2000);
    } catch (e) {
      setMsg(`Error: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/8 p-5 backdrop-blur-sm">
      <p className="mb-1 text-sm font-semibold text-indigo-200">
        Pipeline paused — awaiting Manual Review decisions
      </p>
      <p className="mb-4 text-xs text-indigo-300/60">
        {pendingCount.toLocaleString()} candidate pair
        {pendingCount !== 1 ? "s" : ""} need a steward decision before
        clustering can proceed. Review the queue to make informed decisions on
        each pair.
      </p>

      {msg && <p className="mb-3 text-xs text-indigo-300">{msg}</p>}

      {/* Primary CTA — go to review queue */}
      {onNavigateToReview && (
        <button
          onClick={onNavigateToReview}
          data-testid="mdm-hitl-review-queue"
          className="mb-4 rounded-lg border border-indigo-400/50 bg-indigo-500/25 px-4 py-2 text-sm font-semibold text-indigo-100 transition hover:bg-indigo-500/35"
        >
          Go to Review Queue →
        </button>
      )}

      {/* P5: Admin override — collapsible, requires justification */}
      <details className="group">
        <summary className="cursor-pointer list-none text-xs text-indigo-400/70 hover:text-indigo-300 transition-colors select-none flex items-center gap-1">
          <span className="group-open:hidden">▶</span>
          <span className="hidden group-open:inline">▼</span>
          Admin override — bulk-decide without individual review
        </summary>

        <div className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/8 px-4 py-3">
          <p className="text-xs text-amber-300/80 mb-2 flex items-start gap-1.5">
            <span className="text-amber-400 mt-0.5">⚠</span>
            Bypassing individual pair review overrides the human-in-the-loop
            gate. This action is audited and requires a documented justification
            of at least 10 characters.
          </p>

          <textarea
            value={bypassReason}
            onChange={(e) => setBypassReason(e.target.value)}
            placeholder="Reason for bypassing manual review (e.g. 'Confirmed duplicate-free source — demo environment')…"
            rows={2}
            className="w-full rounded-lg border border-amber-500/30 bg-black/30 px-3 py-2 text-xs text-amber-100 placeholder:text-amber-400/40 outline-none focus:border-amber-400/50 resize-none mb-3"
          />

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleAutoDecide("match")}
              disabled={busy !== null || !bypassAllowed}
              data-testid="mdm-hitl-auto-match"
              className="rounded-lg border border-indigo-500/50 bg-indigo-500/20 px-4 py-2 text-sm font-semibold text-indigo-200 transition hover:bg-indigo-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === "match"
                ? "Processing…"
                : `Auto-match all ${pendingCount.toLocaleString()} pairs & resume`}
            </button>
            <button
              onClick={() => handleAutoDecide("no_match")}
              disabled={busy !== null || !bypassAllowed}
              data-testid="mdm-hitl-auto-decline"
              className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 transition hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === "no_match"
                ? "Processing…"
                : `Auto-decline all ${pendingCount.toLocaleString()} pairs & resume`}
            </button>
          </div>

          {!bypassAllowed && bypassReason.trim().length > 0 && (
            <p className="text-[11px] text-amber-400/70 mt-2">
              Justification must be at least 10 characters (
              {bypassReason.trim().length}/10).
            </p>
          )}
        </div>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

interface MdmRunStatusViewProps {
  runId: string;
  onNavigateToGoldenRecords: () => void;
  onNavigateToReview: () => void;
}

/** Shows after ~30s stuck in 'normalizing' — prompts steward to reset. */
function StuckPipelineBanner({
  runId,
  onReset,
}: {
  runId: string;
  onReset: () => void;
}) {
  const [resetting, setResetting] = useState(false);
  const handleReset = async () => {
    setResetting(true);
    try {
      await resetRun(runId);
      onReset();
    } finally {
      setResetting(false);
    }
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-orange-500/30 bg-orange-500/10 px-5 py-3 text-sm text-orange-200">
      <span className="text-base">⚠</span>
      <span className="flex-1">
        Normalization seems stuck. This can happen on very large sources or
        after a transient error.
      </span>
      <button
        onClick={handleReset}
        disabled={resetting}
        className="rounded-lg border border-orange-400/40 bg-orange-400/20 px-3 py-1.5 text-xs font-semibold text-orange-200 transition hover:bg-orange-400/30 disabled:opacity-50"
      >
        {resetting ? "Resetting…" : "Reset & Retry"}
      </button>
    </div>
  );
}

export function MdmRunStatusView({
  runId,
  onNavigateToGoldenRecords,
  onNavigateToReview,
}: MdmRunStatusViewProps) {
  const {
    runStatus,
    pendingReviewCount,
    stats,
    stages,
    setRun,
    setRunStatus,
    setEntityType,
    setSourceCount,
    setStats,
    setPendingReviewCount,
    updateStage,
    updateStageFromPoll,
    reset,
  } = useMdmRunStore();

  const [run, setRun_] = useState<MdmRun | null>(null);
  const [triggerMode, setTriggerMode] = useState<TriggerMode>("manual");
  const [cronExpr, setCronExpr] = useState("0 2 * * 1-5");
  const [showPreTrigger, setShowPreTrigger] = useState(false);
  const [sseEvents, setSseEvents] = useState<MdmPipelineStageEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rawStatus, setRawStatus] = useState<string>("loading…");
  const esRef = useRef<EventSource | null>(null);
  const stuckCountRef = useRef(0); // consecutive polls stuck in 'normalizing'
  const [showStuckBanner, setShowStuckBanner] = useState(false);

  // Projection values — derived from run config (placeholder estimates)
  const projection: RunProjection = {
    candidatePairs: run?.stats?.candidate_pairs_generated ?? 12_400,
    estimatedHitlSize: run?.stats?.review_pending ?? 1_800,
    estimatedDurationMin: 14,
  };

  const fetchStatus = useCallback(() => {
    getRunStatus(runId)
      .then((r) => {
        setRun_(r);
        setRun(r.id, r.name);
        setRunStatus(r.status);
        if (r.entity_type) setEntityType(r.entity_type);
        setSourceCount(r.source_count);
        setRawStatus(
          (r as unknown as { _raw_status?: string })._raw_status ?? r.status,
        );
        setTriggerMode(r.trigger_mode ?? "manual");

        const rr = r as unknown as {
          _raw_stats?: Record<string, unknown>;
          error_message?: string;
          paused_at?: string;
        };
        const rawStats = rr._raw_stats as Record<string, unknown> | undefined;
        if (r.stats) {
          setStats(r.stats);
          const pending = (rawStats?.review_pending ??
            r.stats.review_pending ??
            0) as number;
          setPendingReviewCount(pending);
        }

        // Derive pipeline stage statuses from run status + stats so the
        // progress card updates on every poll without needing SSE.
        // Use raw DB status for fine-grained stage mapping (r.status is normalized to RunStatus enum).
        const status =
          (rr as unknown as { _raw_status?: string })._raw_status ?? r.status;

        // Stuck pipeline detection: 6 polls (~30s) stuck in 'normalizing'
        // or 'survivorship' (golden-record generation). Both stages run as
        // in-process BackgroundTasks that can silently stall — same banner
        // and reset flow applies to both.
        if (status === "normalizing" || status === "survivorship") {
          stuckCountRef.current += 1;
          if (stuckCountRef.current >= 6) setShowStuckBanner(true);
        } else {
          stuckCountRef.current = 0;
          setShowStuckBanner(false);
        }
        const s = rawStats ?? {};
        const hasPairs =
          ((s.total_pairs ?? s.candidate_pairs_generated ?? 0) as number) > 0;
        const hasGolden =
          ((s.golden_records_created ??
            s.golden_records_updated ??
            0) as number) > 0;
        const hasHitlRouted = ((s.hitl_routed ?? 0) as number) > 0;
        const errorMsg = rr.error_message ?? undefined;
        const isPaused = !!(r as MdmRun & { paused_at?: string | null })
          .paused_at;

        const stageMap = derivePipelineStages(status, {
          isPaused,
          hasPairs,
          hasGolden,
          hasHitlRouted,
        });

        // Push derived stages via poll-aware updater — won't regress stages SSE already advanced.
        for (const [stage, derivedStatus] of Object.entries(stageMap)) {
          updateStageFromPoll(stage, {
            status: derivedStatus,
            error: derivedStatus === "failed" ? errorMsg : undefined,
          });
        }

        setError(null);
      })
      .catch((e: Error) => {
        setError(e.message);
        setRawStatus("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Clear the shared run store before fetching a new run's status — stages/
  // stats/runStatus live in a Zustand store (useMdmRunStore), not local
  // component state, so they otherwise survive navigating from one run to
  // another. reset() already existed for exactly this but was never called
  // anywhere: the previous run's "Complete" stage badges (and
  // updateStageFromPoll's never-regress guard, which then refuses to
  // downgrade a stale "complete" back to "pending"/"running" for the new
  // run) stuck around until a stage in the NEW run happened to reach or
  // exceed the same rank. Must run before the fetch effect below, in a
  // separate effect so it fires first on the same runId change.
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Fetch on mount, then poll every 5s while not terminal
  useEffect(() => {
    fetchStatus();
    const TERMINAL = new Set([
      "completed",
      "failed",
      "error",
      "cancelled",
      "partially_complete",
    ]);
    const id = setInterval(() => {
      if (!TERMINAL.has(runStatus ?? "")) fetchStatus();
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Keep local run state in sync with store
  useEffect(() => {
    if (run && runStatus && run.status !== runStatus) {
      setRun_((prev) => (prev ? { ...prev, status: runStatus } : prev));
    }
  }, [runStatus, run]);

  useEffect(() => {
    if (run && stats && run.stats !== stats) {
      setRun_((prev) => (prev ? { ...prev, stats } : prev));
    }
  }, [stats, run]);

  // SSE subscription
  useEffect(() => {
    if (esRef.current) {
      esRef.current.close();
    }
    const es = new EventSource(`/api/v2/mdm/runs/${runId}/sse`, {
      withCredentials: true,
    });
    esRef.current = es;

    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data) as
          | MdmPipelineStageEvent
          | { type: "badge_update"; count: number };
        if (data.type === "pipeline_stage") {
          const ev = data as MdmPipelineStageEvent;
          setSseEvents((prev) => [...prev, ev]);
          updateStage(ev.stage, {
            status: ev.status,
            records_processed: ev.records_processed,
            duration_ms: ev.duration_ms,
            error: ev.error,
          });
        } else if (data.type === "badge_update") {
          setPendingReviewCount(
            (data as { type: string; count: number }).count,
          );
        }
      } catch {
        // non-JSON frame — ignore
      }
    };

    es.onerror = () => {
      // SSE will auto-reconnect; no action needed
    };

    return () => {
      es.close();
      esRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const handleRunNow = useCallback(() => {
    setShowPreTrigger(true);
  }, []);

  const handleConfirmRun = useCallback(async () => {
    setShowPreTrigger(false);
    try {
      await executeRun(runId);
      setRunStatus("running");
    } catch (e) {
      setError((e as Error).message);
    }
  }, [runId, setRunStatus]);

  const [cancelling, setCancelling] = useState(false);

  const handleCancelRun = useCallback(async () => {
    if (
      !window.confirm(
        "Cancel this run? It won't stop mid-flight work already in progress, " +
          "but it will stop reporting the run as active so you can start a fresh one.",
      )
    ) {
      return;
    }
    setCancelling(true);
    try {
      await cancelRun(runId);
      await fetchStatus();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCancelling(false);
    }
  }, [runId, fetchStatus]);

  const isCompleted =
    runStatus === "completed" ||
    runStatus === "partially_complete" ||
    runStatus === "failed" ||
    rawStatus === "error" ||
    runStatus === "cancelled";

  if (!run) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center gap-4 p-8">
        {error ? (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-4 text-sm text-red-300">
            {error}
            <button
              onClick={fetchStatus}
              className="ml-4 underline opacity-70 hover:opacity-100"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--border-strong)] border-t-indigo-400" />
            <p className="text-sm font-medium text-white/70">
              Loading run status…
            </p>
            <p className="text-xs text-[var(--text-primary)]">
              Run ID: {runId}
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {showPreTrigger && (
        <PreTriggerModal
          projection={projection}
          onConfirm={handleConfirmRun}
          onCancel={() => setShowPreTrigger(false)}
        />
      )}

      <div className="space-y-5">
        {/* Stuck pipeline warning */}
        {showStuckBanner && (
          <StuckPipelineBanner
            runId={runId}
            onReset={() => {
              setShowStuckBanner(false);
              stuckCountRef.current = 0;
              fetchStatus();
            }}
          />
        )}

        {/* Provider-absent notice when LLM adjudication was skipped */}
        {stats?.provider_absent && (
          <div className="flex items-center gap-3 rounded-xl border border-blue-500/20 bg-blue-500/10 px-5 py-3 text-sm text-blue-200">
            <span className="text-base">ℹ</span>
            <span>
              No LLM provider is configured for this project. Pairs will be
              routed to manual review instead of AI adjudication.
            </span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white/90">{run.name}</h2>
            <p className="mt-0.5 text-xs capitalize text-[var(--text-primary)]">
              {run.entity_type} · {run.source_count} source
              {run.source_count !== 1 ? "s" : ""}
            </p>
          </div>
          <span
            className={`
              inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold
              ${runStatus === "running" ? "bg-blue-500/20 text-blue-300 animate-pulse" : ""}
              ${runStatus === "completed" ? "bg-emerald-500/20 text-emerald-300" : ""}
              ${runStatus === "failed" ? "bg-red-500/20 text-red-300" : ""}
              ${runStatus === "ready" ? "bg-[var(--overlay-strong)] text-white/60" : ""}
              ${runStatus === "draft" ? "bg-[var(--overlay-subtle)] text-[var(--text-primary)]" : ""}
              ${runStatus === "partially_complete" ? "bg-amber-500/20 text-amber-300" : ""}
            `}
          >
            {rawStatus}
          </span>
        </div>

        {/* HITL queue CTA */}
        <HitlQueueEntry
          pendingReviewCount={pendingReviewCount}
          runId={runId}
          onNavigateToReview={onNavigateToReview}
        />

        {/* HITL pause banner — shown when pipeline is paused awaiting review decisions */}
        {(rawStatus === "clustering" || run?.paused_at) &&
          pendingReviewCount > 0 && (
            <HitlResumeBanner
              runId={runId}
              pendingCount={pendingReviewCount}
              onResumed={fetchStatus}
              onNavigateToReview={onNavigateToReview}
            />
          )}

        {/* Trigger config */}
        <MdmTriggerConfigPanel
          run={run}
          triggerMode={triggerMode}
          onModeChange={setTriggerMode}
          onRunNow={handleRunNow}
          onCancelRun={handleCancelRun}
          cancelling={cancelling}
          cronExpr={cronExpr}
          onCronChange={setCronExpr}
        />

        {/* Last run card — auto mode only */}
        {triggerMode === "auto" && (
          <LastRunCard
            run={run}
            onNavigateToGoldenRecords={onNavigateToGoldenRecords}
            onRerun={handleRunNow}
          />
        )}

        {/* Pipeline progress — always show */}
        <MdmRunProgressCard
          stages={stages as Record<string, StageState>}
          events={sseEvents}
        />

        {/* Stats summary on completion */}
        {isCompleted && run.stats && (
          <RunStatsSummary
            run={run}
            onNavigateToGoldenRecords={onNavigateToGoldenRecords}
          />
        )}

        {/* Inline error banner */}
        {(error || runStatus === "failed" || rawStatus === "error") && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300">
            {error ??
              (run as unknown as { error_message?: string }).error_message ??
              "Pipeline failed. Start a new run to retry."}
          </div>
        )}
      </div>
    </>
  );
}

export default MdmRunStatusView;
